import React, {useEffect} from 'react';
import clsx from 'clsx';
import moment, {Moment} from '@netx/core/lib/netx-moment';
import {i18n} from '@netx/core-next/locales';
import {dateToHTML} from '@netx/core/lib/underscore-netx';
import {TextField, type TextFieldProps, CircularProgress} from '@mui/material';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {
	DatePicker,
	type DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';
import useSharedRef from '@netx/core-components/hooks/useSharedRef';
import {
	composeClasses,
	styled,
	WithOwnerState,
	type WithClasses,
} from '@netx/core-components/Theme';
import ErrorField from '@netx/core-components/ErrorField';
import {
	dateFieldClasses,
	getDateFieldUtilityClass,
	DATE_FIELD_CLASS_NAME,
	type DateFieldClasses,
} from './dateFieldClasses';

export type DateValue = null | undefined | string;

type DateFieldOwnerState = WithClasses<
	DateFieldClasses,
	{
		fullWidth?: boolean;
	}
>;

const useUtilityClasses = (ownerState: DateFieldOwnerState) => {
	const {classes, fullWidth} = ownerState;
	const slots = {
		root: ['root', fullWidth && 'fullWidth'],
		loader: ['loader'],
	};
	return composeClasses(slots, getDateFieldUtilityClass, classes);
};

const DateFieldRoot = styled('div', {
	name: DATE_FIELD_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}: WithOwnerState<DateFieldOwnerState>) => ({
	display: 'inline-flex',
	flexDirection: 'column',
	...(ownerState?.fullWidth
		? {
				width: '100%',
		  }
		: {}),
	// account for calendar icon button
	'& .MuiInputLabel-formControl:not(.MuiInputLabel-shrink)': {
		maxWidth: 'calc(100% - 56px) !important',
	},
	'& .MuiFormHelperText-root': {
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-all',
	},
}));

const DateFieldLoader = styled(CircularProgress, {
	name: DATE_FIELD_CLASS_NAME,
	slot: 'Loader',
})(({theme}) => ({
	position: 'absolute',
	right: 10,
	top: 10,
	'& circle': {
		strokeWidth: 2,
	},
}));

const getValueFromPickerDate = (date?: Moment | null) => {
	if (!date) return null;

	if (!(date instanceof moment)) return date.toString();

	try {
		return date.format('YYYY-MM-DD');
	} catch (ex) {
		console.error(`error converting date`, date, ex);
	}
	return null;
};

// Note: inputProps will be applied to the datepicker wrapping element and not the input
// super annoying - and hopefully gets worked out someday
// This will also cause other issues, like the value not actually being applied to the input
// For these reasons we cannot allow it to be passed at this time
export interface DateFieldProps
	extends Omit<
			TextFieldProps,
			| 'classes'
			| 'error'
			| 'inputRef'
			| 'label'
			| 'value'
			| 'onChange'
			| 'onError'
		>,
		Omit<
			MuiDatePickerProps<any, any>,
			'classes' | 'label' | 'renderInput' | 'value' | 'onChange'
		>,
		WithClasses<DateFieldClasses> {
	error?: string;
	hideErrorMessage?: boolean;
	label?: string | React.ReactNode;
	loading?: boolean;
	loadingValue?: boolean;
	value?: string | null;
	onChange?: (event: any, value: DateValue) => void;
}

// DatePicker passes a ref to the input but we would also like to have a ref to the input
// need to wrap the text field and share the refs
type DateTextFieldProps = {
	error?: boolean;
	inputRef?: React.Ref<HTMLInputElement>;
} & TextFieldProps;

const DateTextField: React.FunctionComponent<DateTextFieldProps> =
	React.forwardRef<HTMLDivElement, DateTextFieldProps>(
		({error, inputRef, ...props}, ref) => {
			const innerRef = useSharedRef<HTMLInputElement | null>(null, [inputRef]);

			// this is a fun deal!
			useEffect(() => {
				if (innerRef.current)
					innerRef.current.readOnly = props.InputProps?.readOnly === true;
			}, []);

			return (
				<TextField ref={ref} {...props} error={!!error} inputRef={innerRef} />
			);
		},
	);

export const DateFieldPrimitive = (
	{
		classes: _classes,
		className,

		disabled,
		error,
		helperText,
		hideErrorMessage,
		loadingValue,
		name,
		placeholder,
		readOnly,
		required,
		value,
		onChange,
		...props
	}: DateFieldProps,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	value = value == null || value == '' ? null : value;
	if (value && value.includes('/')) {
		value = dateToHTML(value);
	}

	const ownerState: DateFieldOwnerState = {
		classes: _classes,
		className,
		fullWidth: props.fullWidth,
	};

	const classes = useUtilityClasses(ownerState);

	const handleChange = useConstCallback((date: Moment | null) => {
		if (date == null) {
			onChange?.({target: {value: ''}} as any, '');
			return;
		}
		const value = getValueFromPickerDate(date);
		onChange?.({target: {value}} as any, value);
	});

	return (
		<DateFieldRoot
			className={clsx(classes.root, className)}
			ownerState={ownerState}
		>
			<LocalizationProvider
				adapterLocale={i18n.language ?? 'en'}
				dateLibInstance={moment}
				dateAdapter={AdapterMoment}
			>
				<DatePicker
					ref={ref}
					disabled={disabled || loadingValue}
					inputFormat="YYYY-MM-DD"
					onChange={handleChange}
					value={value}
					mask={'____-__-__'}
					minDate={moment('0001-01-01')}
					readOnly={readOnly}
					renderInput={(textFieldProps) => {
						return (
							<DateTextField
								data-testid={(props as any)['data-testid'] as string}
								{...textFieldProps}
								helperText={helperText ?? null}
								fullWidth
								name={name}
								error={!!error || textFieldProps.error}
								required={required}
								size={props.size || textFieldProps.size}
								variant={props.variant || textFieldProps.variant}
								InputProps={{
									...textFieldProps.InputProps,
									...props.InputProps,
									endAdornment: (
										<>
											{loadingValue ? (
												<DateFieldLoader
													size={20}
													className={classes.loader}
													variant="indeterminate"
												/>
											) : (
												textFieldProps.InputProps?.endAdornment
											)}
										</>
									),
								}}
								inputProps={{
									...textFieldProps.inputProps,
									...props.inputProps,
									placeholder:
										placeholder || textFieldProps.inputProps?.placeholder,
								}}
								InputLabelProps={{
									...textFieldProps.InputLabelProps,
									...props.InputLabelProps,
								}}
							/>
						);
					}}
					{...props}
				/>
			</LocalizationProvider>
			{!hideErrorMessage ? (
				<ErrorField disabled={disabled} error={error} />
			) : null}
		</DateFieldRoot>
	);
};

export const DateField = React.forwardRef(DateFieldPrimitive) as (
	props: DateFieldProps,
) => JSX.Element;

export default DateField;
